import { useState } from "react";

import { LeaderboardTable } from "@/components/stats/LeaderboardTable";
import { Card } from "@/components/ui/Card";
import { ErrorMessage } from "@/components/ui/ErrorMessage";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { useAuth } from "@/contexts/auth-context";
import { useLeaderboard } from "@/hooks/use-stats";
import { useWeeklyLeague } from "@/hooks/use-weekly-league";
import { getErrorMessage } from "@/utils/error-message";

const LEAGUE_LABELS: Record<string, string> = {
  sandlot: "Sandlot",
  little_league: "Little League",
  minors: "Minors",
  majors: "Majors",
  world_series: "World Series",
};

const LEAGUE_ICONS: Record<string, string> = {
  sandlot: "\u{26BE}",
  little_league: "\u{1F3DF}\uFE0F",
  minors: "\u{1F396}\uFE0F",
  majors: "\u{2B50}",
  world_series: "\u{1F3C6}",
};

type Tab = "all-time" | "weekly";

export function Leaderboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>("weekly");
  const { data: entries, isLoading: allTimeLoading, error: allTimeError } = useLeaderboard();
  const { data: league, isLoading: leagueLoading, error: leagueError } = useWeeklyLeague();

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-navy-700">Leaderboard</h1>

      <div className="flex gap-2">
        <button
          onClick={() => setTab("weekly")}
          className={`min-h-[44px] rounded-lg px-4 py-2 text-sm font-medium transition-colors
            ${tab === "weekly" ? "bg-navy-700 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
        >
          Weekly League
        </button>
        <button
          onClick={() => setTab("all-time")}
          className={`min-h-[44px] rounded-lg px-4 py-2 text-sm font-medium transition-colors
            ${tab === "all-time" ? "bg-navy-700 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
        >
          All Time
        </button>
      </div>

      {tab === "weekly" && (
        <>
          {leagueLoading && <LoadingSpinner message="Loading weekly league..." />}
          {leagueError && <ErrorMessage message={getErrorMessage(leagueError)} />}

          {league?.userStanding && (
            <Card className="border-2 border-amber-200 bg-amber-50/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{LEAGUE_ICONS[league.userStanding.league] ?? "\u{26BE}"}</span>
                  <div>
                    <p className="text-sm font-bold text-gray-900">
                      {LEAGUE_LABELS[league.userStanding.league] ?? league.userStanding.league}
                    </p>
                    <p className="text-xs text-gray-500">
                      Rank #{league.userStanding.rank} of {league.userStanding.totalInLeague}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-amber-600">
                    {league.userStanding.weeklyXp.toLocaleString()} XP
                  </p>
                  <p className="text-xs text-gray-400">this week</p>
                </div>
              </div>
            </Card>
          )}

          {league && league.standings.length > 0 && (
            <div className="overflow-x-auto rounded-xl bg-white shadow-sm">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    <th className="px-4 py-3">Rank</th>
                    <th className="px-4 py-3">Player</th>
                    <th className="px-4 py-3">League</th>
                    <th className="px-4 py-3 text-right">Weekly XP</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {league.standings.map((entry) => {
                    const isMe = entry.userId === user?.email;
                    return (
                      <tr key={entry.userId} className={isMe ? "bg-navy-50 font-medium" : ""}>
                        <td className="px-4 py-3 text-sm">
                          {entry.rank <= 3 ? (
                            <span className="text-lg">{["\uD83E\uDD47", "\uD83E\uDD48", "\uD83E\uDD49"][entry.rank - 1]}</span>
                          ) : (
                            <span className="text-gray-500">{entry.rank}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {entry.displayName}
                          {isMe && <span className="ml-2 text-xs text-navy-500">(you)</span>}
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                            {LEAGUE_ICONS[entry.league] ?? ""} {LEAGUE_LABELS[entry.league] ?? entry.league}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-bold text-amber-600">
                          {entry.weeklyXp.toLocaleString()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {league && league.standings.length === 0 && (
            <Card className="text-center">
              <p className="text-gray-500">No league activity this week yet. Start playing to join!</p>
            </Card>
          )}

          {!league?.userStanding && !leagueLoading && (
            <Card className="text-center">
              <p className="text-gray-500">Answer a question to join this week's league!</p>
            </Card>
          )}
        </>
      )}

      {tab === "all-time" && (
        <>
          {allTimeLoading && <LoadingSpinner message="Loading leaderboard..." />}
          {allTimeError && <ErrorMessage message={getErrorMessage(allTimeError)} />}

          {entries && entries.length > 0 && (
            <LeaderboardTable entries={entries} currentUserId={user?.sub} />
          )}

          {entries && entries.length === 0 && (
            <p className="py-8 text-center text-sm text-gray-400">
              No leaderboard data yet. Be the first to play!
            </p>
          )}
        </>
      )}
    </div>
  );
}
